/**
 * Enhanced Mobile Navigation
 * Adds modern UX improvements to the mobile menu drawer
 */

class EnhancedMobileNav {
  constructor() {
    this.menuDrawer = document.querySelector('header-drawer');
    this.menuContainer = document.querySelector('#Details-menu-drawer-container');

    if (!this.menuDrawer || !this.menuContainer) return;

    this.init();
  }

  init() {
    this.addSearchToMenu();
    this.addQuickActions();
    this.enhanceMenuInteractions();
    this.addSwipeGestures();
    this.trackMenuAnalytics();
  }

  /**
   * Add search input to mobile menu
   */
  addSearchToMenu() {
    const menuDrawerEl = document.querySelector('.menu-drawer__navigation-container');
    if (!menuDrawerEl) return;

    const searchHTML = `
      <div class="mobile-menu-search">
        <form action="/search" method="get" role="search" class="mobile-menu-search__wrapper">
          <div class="mobile-menu-search__icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="search"
            name="q"
            class="mobile-menu-search__input"
            placeholder="Search products..."
            autocomplete="off"
            aria-label="Search"
          />
        </form>
      </div>
    `;

    menuDrawerEl.insertAdjacentHTML('afterbegin', searchHTML);

    // Auto-focus search when menu opens (optional)
    const searchInput = menuDrawerEl.querySelector('.mobile-menu-search__input');
    this.menuContainer.addEventListener('toggle', () => {
      if (this.menuContainer.open) {
        setTimeout(() => searchInput?.focus(), 350);
      }
    });
  }

  /**
   * Add quick action buttons at top of menu
   */
  addQuickActions() {
    const menuNav = document.querySelector('.menu-drawer__navigation');
    if (!menuNav) return;

    // Check if customer is logged in
    const accountLink = document.querySelector('.header__icon--account')?.href || '';
    const isLoggedIn = accountLink.includes('/account') && !accountLink.includes('login');

    const quickActionsHTML = `
      <div class="mobile-menu-quick-actions">
        ${this.createQuickAction('Account', accountLink, this.getAccountIcon(), 'account')}
        ${this.createQuickAction('Cart', '/cart', this.getCartIcon(), 'cart')}
        ${this.createQuickAction('Search', '#', this.getSearchIcon(), 'search')}
        ${this.createQuickAction('Help', '/pages/contact', this.getHelpIcon(), 'help')}
      </div>
    `;

    menuNav.insertAdjacentHTML('afterbegin', quickActionsHTML);

    // Handle search quick action - focus search input
    const searchAction = menuNav.querySelector('[data-action="search"]');
    searchAction?.addEventListener('click', (e) => {
      e.preventDefault();
      const searchInput = document.querySelector('.mobile-menu-search__input');
      searchInput?.focus();
    });
  }

  /**
   * Create a quick action button
   */
  createQuickAction(label, href, icon, action) {
    return `
      <a href="${href}" class="mobile-menu-quick-action" data-action="${action}">
        <div class="mobile-menu-quick-action__icon">
          ${icon}
        </div>
        <span class="mobile-menu-quick-action__label">${label}</span>
      </a>
    `;
  }

  /**
   * Enhance menu interactions
   */
  enhanceMenuInteractions() {
    // Add breadcrumb to submenus
    this.addBreadcrumbs();

    // Improve submenu back button
    this.enhanceBackButtons();

    // Add keyboard shortcuts
    this.addKeyboardShortcuts();
  }

  /**
   * Add breadcrumb navigation to submenus
   */
  addBreadcrumbs() {
    const submenus = document.querySelectorAll('.menu-drawer__submenu');

    submenus.forEach(submenu => {
      const closeButton = submenu.querySelector('.menu-drawer__close-button');
      if (!closeButton) return;

      const parentText = closeButton.textContent.trim();
      const breadcrumbHTML = `
        <div class="mobile-menu-breadcrumb">
          <a href="#" class="mobile-menu-breadcrumb__link" data-breadcrumb-back>Menu</a>
          <span class="mobile-menu-breadcrumb__separator">›</span>
          <span class="mobile-menu-breadcrumb__current">${parentText}</span>
        </div>
      `;

      submenu.insertAdjacentHTML('afterbegin', breadcrumbHTML);

      // Handle breadcrumb back click
      const backLink = submenu.querySelector('[data-breadcrumb-back]');
      backLink?.addEventListener('click', (e) => {
        e.preventDefault();
        closeButton.click();
      });
    });
  }

  /**
   * Enhance back buttons in submenus
   */
  enhanceBackButtons() {
    const backButtons = document.querySelectorAll('.menu-drawer__close-button');

    backButtons.forEach(button => {
      // Add smooth scroll to top when going back
      button.addEventListener('click', () => {
        const parentMenu = button.closest('.menu-drawer__submenu');
        if (parentMenu) {
          const mainContainer = document.querySelector('.menu-drawer__navigation-container');
          mainContainer?.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    });
  }

  /**
   * Add keyboard shortcuts for menu navigation
   */
  addKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // ESC to close menu
      if (e.key === 'Escape' && this.menuContainer?.open) {
        this.menuContainer.removeAttribute('open');
      }

      // M key to toggle menu (when not in input)
      if (e.key === 'm' && !this.isInputFocused()) {
        const menuSummary = this.menuContainer?.querySelector('summary');
        menuSummary?.click();
      }
    });
  }

  /**
   * Add swipe gestures for mobile
   */
  addSwipeGestures() {
    const menuDrawerEl = document.querySelector('.menu-drawer');
    if (!menuDrawerEl) return;

    let touchStartX = 0;
    let touchEndX = 0;

    menuDrawerEl.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    menuDrawerEl.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipeGesture(touchStartX, touchEndX);
    }, { passive: true });
  }

  /**
   * Handle swipe gesture
   */
  handleSwipeGesture(startX, endX) {
    const swipeThreshold = 100;
    const swipeDistance = startX - endX;

    // Swipe left to close menu
    if (swipeDistance > swipeThreshold) {
      this.menuContainer?.removeAttribute('open');
    }
  }

  /**
   * Track menu analytics
   */
  trackMenuAnalytics() {
    // Track menu open
    const menuSummary = this.menuContainer?.querySelector('summary');
    menuSummary?.addEventListener('click', () => {
      this.trackEvent('mobile_menu_opened');
    });

    // Track menu item clicks
    const menuItems = document.querySelectorAll('.menu-drawer__menu-item');
    menuItems.forEach(item => {
      item.addEventListener('click', (e) => {
        const text = e.currentTarget.textContent.trim();
        this.trackEvent('mobile_menu_item_clicked', { item: text });
      });
    });

    // Track quick action clicks
    const quickActions = document.querySelectorAll('.mobile-menu-quick-action');
    quickActions.forEach(action => {
      action.addEventListener('click', (e) => {
        const actionType = action.getAttribute('data-action');
        this.trackEvent('mobile_menu_quick_action_clicked', { action: actionType });
      });
    });

    // Track search usage
    const searchForm = document.querySelector('.mobile-menu-search__wrapper');
    searchForm?.addEventListener('submit', () => {
      this.trackEvent('mobile_menu_search_used');
    });
  }

  /**
   * Track analytics event
   */
  trackEvent(eventName, properties = {}) {
    // Log to console (replace with your analytics service)
    console.log('[Mobile Nav Analytics]', eventName, properties);

    // Example integrations:

    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, properties);
    }

    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', eventName, properties);
    }

    // Shopify Analytics
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.lib.track(eventName, properties);
    }
  }

  /**
   * Check if an input is currently focused
   */
  isInputFocused() {
    const activeElement = document.activeElement;
    return activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    );
  }

  /**
   * Icon SVGs
   */
  getAccountIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    `;
  }

  getCartIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    `;
  }

  getSearchIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    `;
  }

  getHelpIcon() {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `;
  }
}

/**
 * Mobile Navigation Toggle Animation Enhancement
 */
class MobileNavToggle {
  constructor() {
    this.init();
  }

  init() {
    const menuToggle = document.querySelector('.header__icon--menu');
    if (!menuToggle) return;

    // Add animation class on toggle
    const details = document.querySelector('#Details-menu-drawer-container');
    if (!details) return;

    details.addEventListener('toggle', () => {
      menuToggle.classList.toggle('is-active', details.open);

      // Prevent body scroll when menu is open
      document.body.style.overflow = details.open ? 'hidden' : '';
    });

    // Close menu when clicking outside
    this.addClickOutsideHandler(details);
  }

  addClickOutsideHandler(details) {
    const summary = details.querySelector('summary');

    document.addEventListener('click', (e) => {
      if (!details.open) return;

      const menuDrawer = document.querySelector('.menu-drawer');
      const isClickInside = menuDrawer?.contains(e.target) || summary?.contains(e.target);

      if (!isClickInside) {
        details.removeAttribute('open');
      }
    });
  }
}

/**
 * Initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EnhancedMobileNav();
    new MobileNavToggle();
  });
} else {
  new EnhancedMobileNav();
  new MobileNavToggle();
}
